import { getSupabaseClient } from './supabase';
import type { PrivacyPolicy, PrivacyPolicyAudience } from '@types';

export async function fetchPrivacyPolicy(audience: PrivacyPolicyAudience): Promise<PrivacyPolicy | null> {
  const supabase = getSupabaseClient();

  let query = supabase
    .from('privacy_policy')
    .select('*');

  if (audience === null) {
    query = query.is('audience', null);
  } else {
    query = query.eq('audience', audience);
  }

  const { data, error } = await query.single();

  if (error) {
    // If no row exists, return null (not an error for our purposes)
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching privacy policy:', error);
    throw error;
  }

  return data;
}

export async function upsertPrivacyPolicy(
  audience: PrivacyPolicyAudience,
  content: string
): Promise<PrivacyPolicy> {
  const supabase = getSupabaseClient();

  // First check if a row exists for this audience
  const existing = await fetchPrivacyPolicy(audience);

  if (existing) {
    // Update existing row
    const { data, error } = await supabase
      .from('privacy_policy')
      .update({ content })
      .eq('id', existing.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating privacy policy:', error);
      throw error;
    }

    return data;
  } else {
    // Insert new row
    const { data, error } = await supabase
      .from('privacy_policy')
      .insert({ audience, content })
      .select()
      .single();

    if (error) {
      console.error('Error creating privacy policy:', error);
      throw error;
    }

    return data;
  }
}
